const KEY = "guest_cart";

export type GuestCartItem = {
  variantId: number;
  quantity: number;

  // hydrated fields
  unitPrice?: number;
  productName?: string;
  imageUrl?: string;
};

export type GuestCartState = {
  items: GuestCartItem[];
};

function safeNumber(v: any, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export const guestCart = {
  get(): GuestCartState {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { items: [] };

    try {
      const parsed = JSON.parse(raw);
      const items = Array.isArray(parsed?.items) ? parsed.items : [];
      return {
        items: items.map((i: any) => ({
          variantId: safeNumber(i.variantId),
          quantity: safeNumber(i.quantity),
          unitPrice: i.unitPrice != null ? safeNumber(i.unitPrice) : undefined,
          productName: i.productName ?? undefined,
          imageUrl: i.imageUrl ?? undefined,
        })),
      };
    } catch {
      return { items: [] };
    }
  },

  save(cart: GuestCartState) {
    localStorage.setItem(KEY, JSON.stringify(cart));
  },

  add(item: { variantId: number; quantity: number }) {
    const cart = this.get();
    const vid = safeNumber(item.variantId);
    const qty = safeNumber(item.quantity);

    if (!vid || qty <= 0) return;

    const exist = cart.items.find((i) => i.variantId === vid);

    if (exist) {
      exist.quantity += qty;
    } else {
      cart.items.push({
        variantId: vid,
        quantity: qty,
      });
    }

    this.save(cart);
  },

  update(variantId: number, quantity: number) {
    const cart = this.get();
    const vid = safeNumber(variantId);
    const qty = safeNumber(quantity);

    const item = cart.items.find((i) => i.variantId === vid);
    if (!item) return;

    if (qty <= 0) {
      cart.items = cart.items.filter((i) => i.variantId !== vid);
    } else {
      item.quantity = qty;
    }

    this.save(cart);
  },

  remove(variantId: number) {
    const cart = this.get();
    const vid = safeNumber(variantId);

    cart.items = cart.items.filter((i) => i.variantId !== vid);
    this.save(cart);
  },

  clear() {
    localStorage.removeItem(KEY);
  },

  // ✅ merge preview info vào các item
  hydrateFromPreview(preview: Array<{
    variantId: number;
    unitPrice: number;
    productName?: string | null;
    imageUrl?: string | null;
  }>) {
    const cart = this.get();

    const map = new Map<number, any>();
    for (const p of preview) map.set(Number(p.variantId), p);

    cart.items = cart.items.map((i) => {
      const p = map.get(i.variantId);
      if (!p) return i;

      return {
        ...i,
        unitPrice: safeNumber(p.unitPrice, 0),
        productName: p.productName ?? i.productName,
        imageUrl: p.imageUrl ?? i.imageUrl,
      };
    });

    this.save(cart);
    return cart;
  },
};
